<!-- Backdrop — only visible in expanded (dialog) mode. Stays mounted so
     OnPush + ngIf timing can't make it skip a render. Clicking it pops
     the user back to drawer mode (does not close). -->
<div
    class="gable-backdrop"
    [class.is-visible]="isOpen && isExpanded"
    (click)="onClose()"
    aria-hidden="true"></div>

<div
    class="gable-drawer"
    [class.is-open]="isOpen"
    [class.is-expanded]="isExpanded"
    [ngStyle]="drawerStyle"
    role="dialog"
    aria-label="Gable AI Assistant">

    <!-- Resize handle on left edge — drawer mode only; expanded mode has
         fixed viewport-relative size so resize doesn't apply. -->
    <span
        *ngIf="!isExpanded"
        class="gable-resize-handle"
        appGableDrawerResize
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize drawer"></span>

    <!-- ============================================================ -->
    <!-- HEADER                                                       -->
    <!-- ============================================================ -->
    <header class="gable-header">
        <h2 class="gable-title">
            <span>{{ isEmptyState ? 'New AI chat' : 'Gable AI' }}</span>
            <!-- SD-6742: chat history picker + new-chat affordance ship together
                 with the conversation list dropdown. Keep the title plain
                 until that lands. -->
            <!-- <i class="ph-bold ph-caret-down gable-title-caret icon-muted icon-12" aria-hidden="true"></i> -->
        </h2>
        <flex-spacer></flex-spacer>
        <!-- SD-6742: "new chat" + conversation switcher ship together. Reveal
             this button alongside the title caret. -->
        <!--
        <button
            type="button"
            class="gable-header-btn"
            (click)="onNewChat()"
            aria-label="Start new chat">
            <i class="ph-bold ph-plus icon-muted icon-16" aria-hidden="true"></i>
        </button>
        -->
        <button
            type="button"
            class="gable-header-btn"
            (click)="onToggleDock()"
            [attr.aria-label]="isExpanded ? 'Collapse to drawer' : 'Expand to dialog'">
            <i
                class="ph-bold icon-muted icon-16"
                [class.ph-arrows-out-simple]="!isExpanded"
                [class.ph-arrows-in-simple]="isExpanded"
                aria-hidden="true"></i>
        </button>
        <button
            type="button"
            class="gable-header-btn"
            (click)="onClose()"
            aria-label="Close drawer">
            <i class="ph-bold ph-x icon-muted icon-16" aria-hidden="true"></i>
        </button>
    </header>

    <!-- ============================================================ -->
    <!-- BODY                                                         -->
    <!-- ============================================================ -->
    <section class="gable-body" #conversationScroller (scroll)="onScroll()">

        <!-- EMPTY STATE -->
        <ng-container *ngIf="isEmptyState">
            <div class="gable-empty-state">
                <app-ai-gable-mascot #emptyMascot class="gable-empty-mascot" [size]="40"></app-ai-gable-mascot>
                <h3 class="gable-greeting">Hi, I'm Gable! How can I help…</h3>

                <div class="gable-quick-actions">
                    <button
                        *ngFor="let action of quickActions; trackBy: trackByQuickAction"
                        type="button"
                        class="gable-chip"
                        [class.active]="action.id === activeQuickAction"
                        (click)="onSelectQuickAction(action.id)">
                        <i
                            *ngIf="action.icon"
                            class="ph-bold gable-chip-icon icon-text icon-16"
                            [ngClass]="action.icon"
                            aria-hidden="true"></i>
                        <span>{{ action.label }}</span>
                    </button>
                </div>

                <div class="gable-section-label">Popular at {{ orgName || 'your organization' }}</div>

                <ul class="gable-suggestion-list">
                    <li
                        *ngFor="let suggestion of currentSuggestions; trackBy: trackBySuggestion"
                        class="gable-suggestion-row">
                        <button
                            type="button"
                            class="gable-suggestion-btn"
                            (click)="onSuggestionClick(suggestion)">
                            <i class="ph-bold ph-arrow-bend-down-right gable-suggestion-icon icon-muted icon-12" aria-hidden="true"></i>
                            <span class="gable-suggestion-label">{{ suggestion.label }}</span>
                        </button>
                    </li>
                </ul>
            </div>
        </ng-container>

        <!-- CONVERSATION STATE -->
        <ng-container *ngIf="!isEmptyState">
            <div class="gable-messages">
                <ng-container *ngFor="let msg of messages; let i = index; trackBy: trackByIndex">
                    <!-- USER -->
                    <div class="message user-message"
                         *ngIf="msg.role === 'user'"
                         [attr.data-user-msg-index]="i">
                        <div class="message-bubble">{{ msg.content }}</div>
                    </div>

                    <!-- ASSISTANT -->
                    <div class="message assistant-message" *ngIf="msg.role === 'assistant'">
                        <app-alert-block
                            *ngIf="msg.alert"
                            [type]="msg.alert">
                            {{ msg.content }}
                        </app-alert-block>

                        <ng-container *ngIf="!msg.alert">
                            <ng-container
                                *ngIf="(msg.thoughts && msg.thoughts.length > 0) || !!msg.thinkingContent; else loneGable">
                                <app-ai-thought-trail
                                    class="thought-trail-block"
                                    [thoughts]="msg.thoughts || []"
                                    [thinkingContent]="msg.thinkingContent ?? null"
                                    [streaming]="msg.streamState === 'streaming'"
                                    [elapsedSeconds]="elapsedSeconds(msg)"
                                    [collapsed]="msg.thoughtsCollapsed === true"
                                    (collapsedChange)="onThoughtsCollapsedChange(msg.messageId, $event)">
                                    <app-ai-gable-mascot [size]="24"></app-ai-gable-mascot>
                                </app-ai-thought-trail>
                            </ng-container>
                            <ng-template #loneGable>
                                <app-ai-gable-mascot
                                    class="assistant-mascot"
                                    [size]="24"></app-ai-gable-mascot>
                            </ng-template>

                            <div class="bubble-row" [class.is-streaming]="msg.streamState === 'streaming'">
                                <div
                                    class="message-bubble"
                                    [class.is-streaming]="msg.streamState === 'streaming'"
                                    [class.is-failed]="msg.streamState === 'failed'"
                                    [attr.aria-live]="msg.streamState === 'streaming' ? 'polite' : null">
                                    <span [innerHTML]="msg.content | markdown"></span><span
                                        *ngIf="msg.streamState === 'streaming'"
                                        class="streaming-caret"
                                        aria-hidden="true"></span>
                                </div>
                            </div>

                            <div class="citations" *ngIf="msg.citations && msg.citations.length > 0">
                                <span class="citations-label">Citations</span>
                                <span
                                    *ngFor="let cite of msg.citations; trackBy: trackByCitation"
                                    class="citation-pill">
                                    {{ cite.table }}#{{ cite.id }}
                                </span>
                            </div>

                            <ng-container *ngIf="msg.toolCards && msg.toolCards.length > 0">
                                <ng-container *ngFor="let card of msg.toolCards; trackBy: trackByToolCard">
                                    <app-gable-report-card
                                        *ngIf="asReportCardPayload(card) as reportPayload"
                                        [payload]="reportPayload">
                                    </app-gable-report-card>
                                    <app-gable-budget-redirect-card
                                        *ngIf="asBudgetRedirectPayload(card) as budgetPayload"
                                        [payload]="budgetPayload">
                                    </app-gable-budget-redirect-card>
                                    <app-gable-action-preview-card
                                        *ngIf="asActionPreviewPayload(card) as previewPayload"
                                        [payload]="previewPayload"
                                        (confirm)="onActionPreviewConfirm($event)"
                                        (cancel)="onActionPreviewCancel($event)"
                                        (utilityAction)="onActionPreviewUtility($event)">
                                    </app-gable-action-preview-card>
                                </ng-container>
                            </ng-container>

                            <div class="msg-footer"
                                 *ngIf="msg.streamState === 'complete' && msg.interactionId">
                                <button
                                    type="button"
                                    class="msg-footer-btn"
                                    [class.active]="getFeedback(msg.messageId) === 'good'"
                                    (click)="onFeedback(msg, 'good')"
                                    aria-label="Helpful">
                                    <i class="ph-bold ph-thumbs-up icon-muted icon-16" aria-hidden="true"></i>
                                </button>
                                <button
                                    type="button"
                                    class="msg-footer-btn"
                                    [class.active]="getFeedback(msg.messageId) === 'bad'"
                                    (click)="onFeedback(msg, 'bad')"
                                    aria-label="Not helpful">
                                    <i class="ph-bold ph-thumbs-down icon-muted icon-16" aria-hidden="true"></i>
                                </button>
                            </div>

                            <!-- AI-suggested follow-up prompts. Rendered
                                 only under the most recent assistant
                                 bubble so old follow-ups don't pile up. -->
                            <ul class="gable-followup-list"
                                *ngIf="isLastAssistantMessage(i) && apiSuggestions.length > 0">
                                <li
                                    *ngFor="let suggestion of apiSuggestions; trackBy: trackByApiSuggestion"
                                    class="gable-followup-row">
                                    <button
                                        type="button"
                                        class="gable-followup-btn"
                                        [disabled]="isSending"
                                        (click)="onApiSuggestionClick(suggestion)">
                                        <i class="ph-bold ph-arrow-bend-down-right gable-followup-icon icon-muted icon-12" aria-hidden="true"></i>
                                        <span class="gable-followup-label">{{ suggestion }}</span>
                                    </button>
                                </li>
                            </ul>
                        </ng-container>
                    </div>
                </ng-container>

                <!-- Pre-stream thinking placeholder -->
                <div
                    class="message assistant-message"
                    *ngIf="isSending && !hasStreamingAssistant && (!pendingQuestions || submittingAnswers)">
                    <div class="thinking-bubble">
                        <app-ai-gable-mascot [size]="24"></app-ai-gable-mascot>
                        <span>Thinking…</span>
                    </div>
                </div>

                <!-- Question card -->
                <app-question-card
                    *ngIf="pendingQuestions && !submittingAnswers"
                    [batch]="pendingQuestions"
                    [submitting]="submittingAnswers"
                    (submitAnswers)="onAnswers($event)"
                    (close)="onCloseQuestionCard()">
                </app-question-card>
            </div>

        </ng-container>
    </section>

    <!-- ============================================================ -->
    <!-- FOOTER / COMPOSER                                            -->
    <!-- ============================================================ -->
    <footer class="gable-footer" *ngIf="!pendingQuestions">
        <div class="gable-composer">
            <textarea
                #messageInput
                class="gable-composer-input"
                rows="1"
                [placeholder]="'Ask about ' + (orgName || 'your organization') + '…'"
                [ngModel]="draftMessage"
                (ngModelChange)="onDraftChange($event)"
                (keydown)="onKeydown($event)"
                [disabled]="isSending"></textarea>
            <div class="gable-composer-actions">
                <flex-spacer></flex-spacer>
                <!-- Submit toggles into a Stop button while a stream is
                     in flight, mirroring Claude desktop. Clicking it
                     cancels the in-flight assistant turn. -->
                <button
                    *ngIf="!isSending"
                    type="button"
                    class="gable-send-btn"
                    [disabled]="!draftMessage.trim()"
                    (click)="onSend()"
                    aria-label="Send message">
                    <i class="ph-bold ph-arrow-up icon-16" aria-hidden="true"></i>
                </button>
                <button
                    *ngIf="isSending"
                    type="button"
                    class="gable-send-btn"
                    (click)="onStopStream()"
                    aria-label="Stop response">
                    <i class="ph-bold ph-stop-circle icon-16" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </footer>
</div>
